/**
 * BulletTextWithLineBreaks — experience-bullet text editor with a PDF
 * line-break preview overlay.
 *
 * Click-to-edit textarea that, when not focused, renders the bullet exactly as
 * the PDF generator will wrap it: explicit line breaks (↵), an "(N L)" line
 * count, and amber highlighting of an orphaned last-line word. Line positions
 * come from splitBulletIntoLines (fixed PDF char-width metrics), so they're
 * accurate regardless of the control's rendered width — the RESUME_BULLET_WIDTH
 * sizing just keeps each computed line from visually re-wrapping.
 *
 * Extracted from ResumeEditor so the Resume Scorecard can reuse the same
 * control for bullet findings (ResumeEditor imports ScorecardModal, so the
 * shared piece must live in its own module to avoid a circular import).
 */
import { useState, useRef } from 'react';
import { splitBulletIntoLines, hasOrphan } from '../utils/pdfLineBreaks';

// Resume HTML typography constants — keep in sync with skins/default/style.css.
// Content area is 7.3in; the bullet width subtracts the exp-role + bullet
// indents (+10% to match the wider lightbox).
export const RESUME_FONT = "'Helvetica Neue', Helvetica, Arial, 'Segoe UI', sans-serif";
export const RESUME_BULLET_WIDTH = 743;

export function BulletTextWithLineBreaks({ text, onChange }: { text: string; onChange: (text: string) => void }) {
  const [editing, setEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const lines = splitBulletIntoLines(text);
  const orphan = hasOrphan(lines);
  const multiLine = lines.length > 1;

  if (editing) {
    return (
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        rows={Math.max(2, lines.length + 1)}
        className="px-2 py-1 border border-primary-300 rounded resize-none ring-1 ring-primary-200"
        placeholder="Achievement or responsibility"
        style={{ fontFamily: RESUME_FONT, fontSize: '9.5pt', lineHeight: 1.45, width: RESUME_BULLET_WIDTH }}
        autoFocus
      />
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className="px-2 py-1 border border-gray-200 rounded cursor-text hover:border-gray-300 min-h-[3em]"
      style={{ fontFamily: RESUME_FONT, fontSize: '9.5pt', lineHeight: 1.45, width: RESUME_BULLET_WIDTH }}
      title={orphan ? 'Last line has an orphaned word — consider editing to reflow' : `${lines.length} line${lines.length !== 1 ? 's' : ''} in PDF`}
    >
      {!text ? (
        <span className="text-gray-400">Achievement or responsibility</span>
      ) : (
        lines.map((line, i) => {
          const isLast = i === lines.length - 1;
          const isOrphanLine = isLast && orphan;
          return (
            <span key={i}>
              <span className={isOrphanLine ? 'bg-amber-100 text-amber-800 rounded px-0.5' : ''}>
                {line}
              </span>
              {!isLast && (
                <>
                  <span className="text-gray-300 text-xs mx-0.5 select-none">↵</span>
                  <br />
                </>
              )}
            </span>
          );
        })
      )}
      {multiLine && (
        <span className={`ml-1 text-xs select-none ${orphan ? 'text-amber-600' : 'text-gray-400'}`}>
          ({lines.length}L{orphan ? ' · orphan' : ''})
        </span>
      )}
    </div>
  );
}
